import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/chatIdentity';

const API_BASE_URL = getApiBaseUrl();
const CHAT_API_BASE_URL = `${API_BASE_URL}/api/chat`;

const HomePage = () => {
    const navigate = useNavigate();
    const [chatInput, setChatInput] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const [isTranscribingVoiceDraft, setIsTranscribingVoiceDraft] = useState(false);
    const [pendingVoiceDraft, setPendingVoiceDraft] = useState(false);
    const fileInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingTimerRef = useRef(null);
    const recordingActionRef = useRef('discard');

    useEffect(() => {
        return () => {
            // Stop and cleanup media recorder if active
            try {
                if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                    mediaRecorderRef.current.stop();
                }
            } catch (e) {
                // ignore
            }

            // Stop any active tracks
            try {
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach((t) => t.stop());
                    streamRef.current = null;
                }
            } catch (e) {}

            // Clear timer
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
            }
        };
    }, []);

    const formatTime = (s) => {
        const mins = Math.floor(s / 60);
        const secs = s % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const startRecording = async () => {
        try {
            if (isRecording) return; // guard against re-entrancy
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const recorder = new MediaRecorder(stream);
            audioChunksRef.current = [];
            recordingActionRef.current = 'discard';
            
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };

            recorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const nextAction = recordingActionRef.current;
                recordingActionRef.current = 'discard';
                // stop stream tracks from ref
                try {
                    if (streamRef.current) {
                        streamRef.current.getTracks().forEach(track => track.stop());
                        streamRef.current = null;
                    }
                } catch (e) {}

                if (audioBlob.size === 0) {
                    audioChunksRef.current = [];
                    return;
                }

                if (nextAction === 'send') {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const base64 = reader.result.split(',')[1];
                        const voiceAttachment = {
                            filename: 'voice_note.webm',
                            mime_type: 'audio/webm',
                            base64: base64
                        };
                        navigate('/chat', { 
                            state: { 
                                attachments: [voiceAttachment], 
                                wasVoice: true 
                            } 
                        });
                        audioChunksRef.current = [];
                    };
                    reader.readAsDataURL(audioBlob);
                    return;
                }

                if (nextAction === 'transcribe') {
                    await transcribeVoiceDraft(audioBlob);
                }

                audioChunksRef.current = [];
            };

            recorder.start();
            mediaRecorderRef.current = recorder;
            setIsRecording(true);
            setRecordingSeconds(0);
            recordingTimerRef.current = setInterval(() => {
                setRecordingSeconds(prev => prev + 1);
            }, 1000);
        } catch (err) {
            console.error("Error accessing microphone:", err);
            alert("Could not access microphone on the home page. Please check permissions.");
        }
    };

    const transcribeVoiceDraft = async (audioBlob) => {
        setIsTranscribingVoiceDraft(true);

        try {
            const audio = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    try {
                        resolve({
                            filename: 'voice_note.webm',
                            mime_type: 'audio/webm',
                            base64: reader.result.split(',')[1]
                        });
                    } catch (error) {
                        reject(error);
                    }
                };
                reader.onerror = reject;
                reader.readAsDataURL(audioBlob);
            });

            const response = await fetch(`${CHAT_API_BASE_URL}/voice/transcribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ audio }),
            });

            if (!response.ok) {
                throw new Error('Failed to transcribe voice note');
            }

            const data = await response.json();
            const transcript = (data?.transcript || '').trim();

            if (!transcript) {
                throw new Error('Voice transcription returned empty text');
            }

            setChatInput(transcript);
            setPendingVoiceDraft(true);
        } catch (error) {
            console.error('Voice transcription failed:', error);
            alert('Could not transcribe your voice note. Please try again.');
        } finally {
            setIsTranscribingVoiceDraft(false);
        }
    };

    const stopRecording = (action = 'discard') => {
        if (mediaRecorderRef.current && isRecording) {
            recordingActionRef.current = action;
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
            }
        }
    };

    const sendRecording = () => {
        stopRecording('send');
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        
        if (attachments.length + files.length > 10) {
            alert('You can upload a maximum of 10 files at once.');
            return;
        }

        files.forEach(file => {
            if (file.size > 10 * 1024 * 1024) {
                alert(`File ${file.name} exceeds the 10MB limit.`);
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setAttachments(prev => [...prev, {
                    id: Math.random().toString(36).substr(2, 9),
                    filename: file.name,
                    mime_type: file.type || 'application/octet-stream',
                    base64: reader.result.split(',')[1]
                }]);
            };
            reader.readAsDataURL(file);
        });
        e.target.value = null;
    };

    const removeAttachment = (id) => {
        setAttachments(prev => prev.filter(a => a.id !== id));
    };

    const handleChatSubmit = (e) => {
        e.preventDefault();
        if (chatInput.trim() || attachments.length > 0) {
            navigate('/chat', { 
                state: { 
                    q: chatInput.trim(), 
                    attachments: attachments.length > 0 ? attachments : undefined,
                    wasVoice: pendingVoiceDraft
                } 
            });
        }
    };

    const handleSuggestedTask = (task) => {
        navigate('/chat', { state: { q: task } });
    };

    return (
        <main className="font-nunito">
            {/* ===== SECTION 1: HERO ===== */}
            <section className="hero-gradient min-h-[80vh] flex items-center justify-center text-center px-6 sm:px-8 py-16 overflow-hidden relative">
                <div className="max-w-4xl mx-auto relative z-10 flex flex-col items-center w-full">
                    {/* Badge */}
                    <div className="mb-6 animate-in fade-in slide-in-from-bottom-4 duration-1000">
                        <span className="px-3 py-1 bg-white/5 text-white/85 rounded-full text-[10px] font-black uppercase tracking-[0.2em] border border-[#D81F26]/60">
                            The Future of Vehicle Care
                        </span>
                    </div>
                    
                    <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-white leading-[1.1] mb-6 tracking-tight animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-150">
                        Car Problem? <br />
                        <span className="text-[#D81F26]">Get answers in seconds.</span>
                    </h1>
                    
                    <p className="text-base sm:text-lg md:text-xl text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed font-black animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300">
                        Describe what’s happening and get a simple, reliable answer like chatting with a mechanic you trust.
                    </p>
                    
                    {/* Premium Chat Input Bar */}
                    <div className="w-full max-w-2xl px-4 sm:px-0 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-500">
                        {/* Attachment Previews */}
                        {attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                {attachments.map(file => (
                                    <div 
                                        key={file.id} 
                                        className="flex items-center gap-2 bg-[#F9F9F9] border border-black/5 px-3 py-1.5 rounded-xl shadow-sm group hover:border-black/20 transition-all"
                                    >
                                        <span className="text-[10px] font-black text-black/70 uppercase tracking-widest truncate max-w-[120px]">
                                            {file.filename}
                                        </span>
                                        <button 
                                            onClick={() => removeAttachment(file.id)}
                                            aria-label={`Remove ${file.filename}`}
                                            className="w-4 h-4 flex items-center justify-center rounded-full bg-black/5 text-black hover:bg-black hover:text-gray-100 transition-colors"
                                        >
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <form 
                            onSubmit={handleChatSubmit}
                            className={`bg-white rounded-[24px] p-1.5 pr-2.5 flex items-center shadow-[0_15px_40px_rgba(0,0,0,0.25)] border border-white/10 focus-within:border-[#D81F26] focus-within:shadow-[0_15px_40px_rgba(0,0,0,0.35)] transition-all duration-500 group ${isRecording ? 'ring-2 ring-red-500/50' : ''}`}
                        >
                            <input 
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                multiple
                                onChange={handleFileChange}
                                accept="image/*,.pdf,.doc,.docx,.txt"
                            />
                            
                            {isRecording && (
                                <div className="absolute inset-1.5 bg-[#F9F9F9]/95 backdrop-blur-md rounded-[20px] flex items-center px-4 gap-4 animate-in fade-in shadow-[0_4px_20px_rgba(239,68,68,0.08)] z-20">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                                        <span className="text-[11px] font-black text-red-500 tabular-nums">{formatTime(recordingSeconds)}</span>
                                    </div>
                                    <div className="flex-1 flex gap-0.5 justify-center items-center h-10">
                                        {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16].map(i => (
                                            <div key={i} className="w-1 bg-red-400/40 rounded-full animate-voice-bar" style={{ height: `${Math.random() * 12 + 6}px`, animationDelay: `${i * 0.05}s` }} />
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <button 
                                            type="button"
                                            onClick={() => stopRecording('transcribe')}
                                            aria-label="Stop recording and transcribe"
                                            className="h-9 px-3 flex items-center justify-center gap-2 text-black/50 hover:text-red-500 rounded-full hover:bg-red-50 transition-all border border-red-100/80 bg-white/80"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                <rect x="6" y="6" width="12" height="12" rx="2" />
                                            </svg>
                                            <span className="text-[10px] font-black uppercase tracking-[0.18em]">Stop</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={sendRecording}
                                            aria-label="Send recording"
                                            className="h-9 px-3 flex items-center justify-center gap-2 rounded-full bg-black text-white shadow-[0_4px_14px_rgba(0,0,0,0.18)] hover:scale-[1.02] active:scale-95 transition-all"
                                        >
                                            <span className="text-[10px] font-black uppercase tracking-[0.18em]">Send</span>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <path d="m5 12 7-7 7 7"/>
                                                <path d="M12 19V5"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            <button 
                                type="button"
                                onClick={() => fileInputRef.current.click()}
                                aria-label="Attach files"
                                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-neutral-50 transition-colors text-neutral-300 group-focus-within:text-black"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                            </button>
                            <input 
                                type="text"
                                value={chatInput}
                                onChange={(e) => {
                                    const nextValue = e.target.value;
                                    setChatInput(nextValue);
                                    if (pendingVoiceDraft && !nextValue.trim()) {
                                        setPendingVoiceDraft(false);
                                    }
                                }}
                                placeholder="Tell CarFido what's happening..."
                                disabled={isTranscribingVoiceDraft}
                                className="flex-1 bg-transparent border-none outline-none px-3 py-2.5 text-base text-black placeholder:text-neutral-300 font-semibold tracking-tight"
                            />
                            <div className="flex items-center gap-1">
                                <button 
                                    type="button"
                                    onClick={startRecording}
                                    disabled={isTranscribingVoiceDraft}
                                    aria-label={isRecording ? 'Recording in progress' : 'Start voice recording'}
                                    className={`w-10 h-10 flex items-center justify-center rounded-full transition-all active:scale-95 ${
                                        isTranscribingVoiceDraft
                                            ? 'text-neutral-200 cursor-not-allowed'
                                            : 'text-neutral-300 hover:bg-neutral-50 hover:text-black'
                                    }`}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                                </button>
                                <button 
                                    type="submit"
                                    aria-label="Send message"
                                    disabled={isTranscribingVoiceDraft || (!chatInput.trim() && attachments.length === 0)}
                                    className={`w-10 h-10 flex items-center justify-center rounded-full transition-all ${
                                        isTranscribingVoiceDraft || (!chatInput.trim() && attachments.length === 0)
                                            ? 'bg-neutral-100 text-neutral-300'
                                            : 'bg-[#D81F26] text-white hover:bg-[#B8181E] hover:scale-105 active:scale-95'
                                    }`}
                                >
                                    {isTranscribingVoiceDraft ? (
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 7-7 7 7"/><path d="M12 19V5"/></svg>
                                    )}
                                </button>
                            </div>
                        </form>
                        {/* Suggested Tasks Bubbles */}
                        <div className="flex flex-wrap justify-center gap-2 mt-6 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-700">
                            {[
                                "Diagnose a sound",
                                "Check engine light",
                                "Maintenance schedule",
                                "Brake symptoms",
                                "Fuel efficiency help"
                            ].map((task) => (
                                <button
                                    key={task}
                                    onClick={() => handleSuggestedTask(task)}
                                    aria-label={`Suggest: ${task}`}
                                    className="px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-[10px] font-black uppercase tracking-widest text-white/80 hover:bg-[#D81F26]/10 hover:text-white hover:border-[#D81F26] transition-all active:scale-95 shadow-sm"
                                >
                                    {task}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-16 flex flex-col items-center animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-1000">
                        <p className="text-[10px] text-white/30 font-black uppercase tracking-[0.4em] mb-4">
                            Scroll to explore
                        </p>
                        <div className="w-px h-10 bg-gradient-to-b from-[#D81F26]/50 to-transparent"></div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 2: CAR PROBLEMS ===== */}
            <section className="bg-[#F9F9F9] px-8 py-24 border-t border-[#E6E6E6]">
                <div className="max-w-4xl mx-auto text-center">
                    <span className="text-[10px] font-black text-[#D81F26] uppercase tracking-[0.3em] mb-4 block">The Gap in Knowledge</span>
                    <h2 className="text-3xl md:text-5xl font-black text-black mb-6 leading-[1.1] tracking-tighter">
                        Most car owners guess. <br />
                        <span className="text-[#D81F26]">That’s where things go wrong.</span>
                    </h2>
                    <p className="text-base md:text-lg text-black/80 mb-12 max-w-xl mx-auto font-medium leading-relaxed">
                        When you don’t fully understand the problem, it’s easy to overpay or fix the wrong thing. CarFido closes that gap.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 text-left">
                        <div className="bg-white p-8 rounded-[32px] border border-black/5 hover:shadow-lg transition-all duration-500">
                            <ul className="space-y-5">
                                {[
                                    'Warning lights with no clear explanation',
                                    'Mechanics suggesting costly repairs you’re unsure about',
                                    'Maintenance is getting delayed or forgotten',
                                    'Small issues turning into expensive ones',
                                    'Confusion about which repairs are truly urgent',
                                    'Difficulty explaining issues clearly to service centers',
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-black">
                                        <div className="w-4 h-4 rounded-full bg-[#F2F2F2] flex items-center justify-center mt-1 shrink-0">
                                            <div className="w-1.5 h-1.5 bg-[#D81F26] rounded-full" />
                                        </div>
                                        <span className="text-sm font-black text-black/70 leading-snug">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="relative group">
                            <img
                                src="/images/Home_1.jpeg"
                                alt="Car problems illustration"
                                className="w-full h-full object-cover rounded-[32px] shadow-xl group-hover:scale-[1.02] transition-transform duration-700"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: MEET DASH ===== */}
            <section className="dark-band px-8 py-24 overflow-hidden">
                <div className="max-w-4xl mx-auto flex flex-col items-center text-center">
                    <span className="text-[10px] font-black text-[#D81F26] uppercase tracking-[0.3em] mb-4 block">Your Car's Best Friend</span>
                    <h2 className="text-3xl md:text-5xl font-black text-white mb-6 leading-[1.1] tracking-tight">
                        Meet CarFido. <br />
                        <span className="text-white/60">Your AI vehicle assistant.</span>
                    </h2>
                    <p className="text-base md:text-lg text-white/70 mb-12 max-w-xl mx-auto font-medium leading-relaxed">
                        Just send a message. CarFido explains your problem in simple terms and tells you what to do next.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
                        {[
                            { title: 'Understand issues', desc: 'Describe symptoms and get clear causes instantly.', icon: <React.Fragment><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></React.Fragment> },
                            { title: 'Repair Guidance', desc: 'Get guidance for DIY fixes or talking to a mechanic.', icon: <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/> },
                            { title: 'Track History', desc: 'Keep service history and past issues organized in one place.', icon: <path d="M12 20v-6M9 20v-10M15 20v-2M18 20v-8M21 20v-5M6 20v-7M3 20v-11"/> },
                        ].map((feature) => (
                            <div key={feature.title} className="bg-white p-6 rounded-[32px] border border-white/10 hover:shadow-lg transition-all duration-500 text-left flex flex-col items-start group">
                                <div className="w-10 h-10 rounded-xl bg-[#D81F26]/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D81F26" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">{feature.icon}</svg>
                                </div>
                                <h4 className="font-black text-black text-lg mb-2 tracking-tight">{feature.title}</h4>
                                <p className="text-xs text-black/80 font-black leading-relaxed">{feature.desc}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 w-full">
                        <div className="bg-white rounded-[40px] p-8 border border-white/10 flex flex-col md:flex-row items-center gap-10 shadow-xl relative overflow-hidden group">
                            <div className="flex-1 text-left relative z-10">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-2 h-2 bg-[#1e954aff] rounded-full" />
                                    <p className="text-[10px] font-black text-[#1e954aff] uppercase tracking-[0.2em]">Direct Connection</p>
                                </div>
                                <h3 className="text-2xl md:text-4xl font-black text-black mb-4 tracking-tight">
                                    Available on <br />
                                    <span className="text-[#1e954aff]">WhatsApp</span>
                                </h3>
                                <p className="text-sm text-black/80 font-medium mb-6 max-w-sm">Scan the QR code to get started instantly on your phone.</p>
                                <a href="https://wa.me/your_number" target="_blank" rel="noopener noreferrer" aria-label="Start chat on WhatsApp" className="px-6 py-2.5 bg-[#1F1F1F] text-white rounded-full text-xs font-black uppercase tracking-widest hover:bg-[#414141] transition-all shadow-md">Start Chatting</a>
                            </div>
                            <div className="shrink-0 relative z-10">
                                <div className="bg-white p-3 rounded-[32px] shadow-lg border-2 border-black/5">
                                    <img src="/qr_black_on_white.png" alt="WhatsApp QR" className="w-32 h-32 md:w-40 md:h-40" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: HOW DASH WORKS ===== */}
            <section className="bg-[#F9F9F9] px-8 py-24 border-t border-[#E6E6E6]">
                <div className="max-w-4xl mx-auto text-center">
                    <span className="text-[10px] font-black text-[#D81F26] uppercase tracking-[0.3em] mb-4 block">Simple Workflow</span>
                    <h2 className="text-3xl md:text-5xl font-black text-black mb-12 leading-[1.1] tracking-tighter">
                        Just describe it. <br />
                        <span className="text-[#6B6B6B]">We’ll do the rest.</span>
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                        <div className="space-y-10 text-left">
                            {[
                                { step: '1', title: 'Describe symptoms', desc: 'Message CarFido what’s happening. "My car vibrates when I brake."' },
                                { step: '2', title: 'Get explanations', desc: 'CarFido tells you what’s likely causing the issue clearly.' },
                                { step: '3', title: 'Plan your next step', desc: 'Know if it’s a quick DIY fix or if you need to call a pro.' },
                                { step: '4', title: 'History saved', desc: 'All your chats are automatically organized.' },
                            ].map((item) => (
                                <div key={item.step} className="flex gap-5 group">
                                    <div className="w-10 h-10 rounded-xl bg-[#1F1F1F] text-white flex items-center justify-center text-lg font-black shrink-0 shadow-md group-hover:scale-110 transition-all">
                                        {item.step}
                                    </div>
                                    <div>
                                        <h4 className="font-black text-black text-lg mb-1 tracking-tight">{item.title}</h4>
                                        <p className="text-xs text-black/80 font-black leading-relaxed">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="relative">
                            <div className="absolute inset-0 bg-[#D81F26]/10 blur-[60px] rounded-full" />
                            <img
                                src="/dash_chat.png"
                                alt="CarFido Chat Preview"
                                className="w-full max-w-[280px] mx-auto rounded-[40px] shadow-2xl border-[10px] border-white relative z-10"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: EVERYTHING YOU NEED ===== */}
            <section className="bg-white px-8 py-24 border-t border-[#E6E6E6]">
                <div className="max-w-4xl mx-auto text-center">
                    <span className="text-[10px] font-black text-[#D81F26] uppercase tracking-[0.3em] mb-4 block">Unified Experience</span>
                    <h2 className="text-3xl md:text-5xl font-black text-black mb-12 leading-[1.1] tracking-tighter">
                        Everything you need <br />
                        <span className="text-[#6B6B6B]">to manage your vehicle.</span>
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                        {[
                            { title: 'Understand', desc: 'Ask in plain language. Get clear cost estimates.', icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/> },
                            { title: 'Fix', desc: 'Step-by-step guidance. Walk into the repair shop informed.', icon: <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/> },
                            { title: 'Track', desc: 'Keep service history and all your chats organized.', icon: <path d="M12 20v-6M9 20v-10M15 20v-2M18 20v-8M21 20v-5M6 20v-7M3 20v-11"/> },
                            { title: 'Save', desc: 'Reference your vehicle history anywhere, anytime.', icon: <React.Fragment><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></React.Fragment> },
                        ].map((item) => (
                            <div key={item.title} className="bg-[#F9F9F9] p-8 rounded-[32px] border border-black/[0.08] hover:shadow-2xl hover:border-[#D81F26]/30 transition-all duration-500 group shadow-sm">
                                <div className="w-10 h-10 rounded-xl bg-[#414141] text-gray-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-all shadow-lg shadow-[#414141]/20">
                                     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">{item.icon}</svg>
                                </div>
                                <h4 className="text-xl font-black text-black mb-2 tracking-tight">{item.title}</h4>
                                <p className="text-sm text-black/80 font-black leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: BUILT FOR EVERY DRIVER ===== */}
            <section className="bg-[#F9F9F9] px-8 py-24 border-t border-[#E6E6E6]">
                <div className="max-w-4xl mx-auto text-center">
                    <span className="text-[10px] font-black text-[#D81F26] uppercase tracking-[0.3em] mb-4 block">Made for You</span>
                    <h2 className="text-3xl md:text-5xl font-black text-black mb-12 leading-[1.1] tracking-tighter">
                        Built for every <br />
                        <span className="text-[#6B6B6B]">type of driver.</span>
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { title: 'Daily drivers', desc: 'Avoid getting overcharged.', image: '/images/resize1.jpeg' },
                            { title: 'DIY enthusiasts', desc: 'Get guidance instantly.', image: '/images/resize2.jpeg' },
                            { title: 'Buyers & sellers', desc: 'Keep a clear history.', image: '/images/resize4.jpeg' },
                        ].map((driver) => (
                            <div key={driver.title} className="flex flex-col group items-center">
                                <div className="bg-white rounded-[32px] mb-6 overflow-hidden aspect-square w-full flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-700 border border-black/5">
                                    <img src={driver.image} alt={driver.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                                </div>
                                <h4 className="font-black text-black text-lg mb-1 tracking-tight">{driver.title}</h4>
                                <p className="text-[10px] text-black/80 font-black tracking-tight uppercase">{driver.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: CLEAN ANSWERS ===== */}
            <section className="dark-band px-8 py-24">
                <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-16">
                    <div className="flex-1 relative">
                        <img
                            src="/images/Home_5.jpeg"
                            alt="Expert Diagnostics"
                            className="w-full h-full object-cover rounded-[40px] shadow-xl relative z-10"
                        />
                    </div>
                    <div className="flex-1 text-left">
                        <span className="text-[10px] font-black text-[#D81F26] uppercase tracking-[0.3em] mb-4 block">Expert Knowledge</span>
                        <h2 className="text-3xl md:text-4xl font-black text-white mb-6 leading-[1.1] tracking-tighter">
                            Clean answers <br />
                            <span className="text-white/60">you can trust.</span>
                        </h2>
                        <p className="text-base text-white/70 font-medium mb-6 leading-relaxed">
                            CarFido is built using real automotive diagnostics knowledge and data used by repair professionals.
                        </p>
                        <p className="text-base text-white/70 font-medium leading-relaxed">
                            Our mission: Give every driver the knowledge of a master mechanic.
                        </p>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 8: FUTURE OF VEHICLE OWNERSHIP ===== */}
            <section className="bg-white px-8 py-24">
                <div className="max-w-4xl mx-auto text-center">
                    <span className="text-[10px] font-black text-[#D81F26] uppercase tracking-[0.3em] mb-4 block">Innovation</span>
                    <h2 className="text-3xl md:text-5xl font-black text-black mb-12 leading-[1.1] tracking-tighter">
                        The future of <br />
                        <span className="text-[#6B6B6B]">vehicle ownership.</span>
                    </h2>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                        {[
                            { title: 'Repair Shops', desc: 'Book top mechanics.', icon: <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/> },
                            { title: 'Parts Ordering', desc: 'Automatic delivery.', icon: <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/> },
                            { title: 'Health Insights', desc: 'Proactive monitoring.', icon: <path d="M22 12h-4l-3 9L9 3l-3 9H2"/> },
                            { title: 'Resale Reports', desc: 'Certified history.', icon: <React.Fragment><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></React.Fragment> },
                            { title: 'Smart Planning', desc: 'Service-integrated nav.', icon: <React.Fragment><circle cx="12" cy="12" r="10"/><path d="m16.2 7.8-2 2.6c-.4.5-1 .8-1.7.8-.7 0-1.3-.3-1.7-.8l-2-2.6"/></React.Fragment> },
                            { title: 'Breakdown Help', desc: 'Stranded guidance.', icon: <React.Fragment><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></React.Fragment> },
                        ].map((item) => (
                            <div key={item.title} className="bg-[#fafafa] p-5 rounded-[24px] border border-black/[0.08] hover:bg-[#1F1F1F] group transition-all duration-500 text-left shadow-sm">
                                <div className="w-8 h-8 rounded-lg bg-white text-[#414141] group-hover:text-[#D81F26] flex items-center justify-center mb-3 shadow-sm group-hover:scale-110 transition-transform">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">{item.icon}</svg>
                                </div>
                                <h4 className="text-[13px] font-black text-black group-hover:text-gray-100 mb-0.5 tracking-tight">{item.title}</h4>
                                <p className="text-[10px] text-black/80 group-hover:text-white/60 font-black leading-snug">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="bg-[#F9F9F9] px-8 py-24 text-center">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-gradient-to-br from-[#1F1F1F] via-[#2A2A2A] to-[#414141] rounded-[48px] p-12 md:p-20 shadow-2xl relative overflow-hidden group">
                        {/* Decorative background elements */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#F9F9F9]/5 rounded-full -mr-32 -mt-32 transition-transform group-hover:scale-110 duration-700" />
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full -ml-24 -mb-24 transition-transform group-hover:scale-110 duration-700" />
                        
                        <div className="relative z-10">
                            <span className="text-[10px] font-black text-[#D81F26] uppercase tracking-[0.4em] mb-6 block">Ready to start?</span>
                            <h2 className="text-4xl md:text-6xl font-black text-gray-100 mb-10 tracking-tighter leading-tight">
                                Experience the <br className="hidden md:block" /> future of vehicle care.
                            </h2>
                            <button 
                                onClick={() => navigate('/chat')}
                                className="bg-[#D81F26] text-white px-12 py-5 rounded-full text-sm font-black uppercase tracking-widest hover:bg-[#B8181E] hover:scale-105 active:scale-95 transition-all shadow-[0_20px_50px_rgba(0,0,0,0.2)]"
                            >
                                Start Chatting with CarFido
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default HomePage;
